<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import AppCard from "@/components/AppCard";
import AppTicket from "@/components/Tickets/AppTicket";
import { store } from "@/store";
import { isBlank } from "@/utils/formatters";
import useApi from "@/composables/useApi";

const tickets = ref([]);
const filter = ref("open");
const { t } = useI18n();

const { getTickets } = useApi().ticket;

const openTickets = computed(() =>
  tickets.value.filter((ticket) => isBlank(ticket.close_date))
);

const closedTickets = computed(() =>
  tickets.value.filter((ticket) => !isBlank(ticket.close_date))
);

const filters = computed(() => [
  {
    value: "open",
    label: t("ticket.open"),
    icon: "uil uil-comment-alt-lines",
    count: openTickets.value.length,
  },
  {
    value: "closed",
    label: t("ticket.closed"),
    icon: "uil uil-check-circle",
    count: closedTickets.value.length,
  },
]);

const visibleTickets = computed(() =>
  filter.value === "closed" ? closedTickets.value : openTickets.value
);

const loadTickets = async () => {
  const data = await getTickets(store.user.id);
  return data.payload;
};

onMounted(async () => {
  try {
    store.APP_LOADING = true;
    tickets.value = (await loadTickets()) || [];
  } catch (error) {
    console.log(error);
  } finally {
    store.APP_LOADING = false;
  }
});
</script>
<template lang="pug">
.support
  .support__toolbar
    .filters(role="tablist" :aria-label="$t('invoices.filter_by')")
      button.filters__tab(
        v-for="item in filters"
        :key="item.value"
        type="button"
        role="tab"
        :aria-selected="filter === item.value"
        :class="{ 'is-active': filter === item.value }"
        @click="filter = item.value"
      )
        i(:class="item.icon" aria-hidden="true")
        span {{ item.label }}
        span.filters__count {{ item.count }}

  .support__body(v-if="visibleTickets.length")
    app-ticket(v-for="ticket in visibleTickets" :ticket="ticket" :key="ticket.id")
  AppCard.support__empty(v-else padding="lg")
    i.uil.uil-comment-alt-question(aria-hidden="true")
    h3 {{ $t("ticket.empty_title") }}
    p {{ $t("ticket.empty_subtitle") }}
</template>

<style lang="scss" scoped>
.support {
  @apply pb-6;

  &__toolbar {
    @apply flex;
    @apply flex-col;
    @apply gap-3;
    @apply mb-4;
    @screen sm {
      @apply flex-row;
      @apply items-center;
      @apply justify-between;
    }
  }

  &__new {
    @apply inline-flex;
    @apply items-center;
    @apply justify-center;
    @apply gap-2;
    @apply px-4;
    @apply py-2.5;
    @apply rounded-lg;
    @apply bg-gignetblue-400;
    @apply text-white;
    @apply text-sm;
    @apply font-semibold;
    @apply whitespace-nowrap;
    @apply outline-none;
    @apply shadow-gignetblue-400-md;
    @apply transition-all;
    @apply duration-200;

    i {
      @apply text-lg;
      @apply leading-none;
    }

    &:hover {
      @apply bg-gignetblue-500;
      @apply shadow-gignetblue-400-lg;
    }

    &:focus-visible {
      @apply ring;
      @apply ring-gignetblue-300;
      @apply ring-offset-2;
      @apply ring-opacity-50;
    }
  }

  &__body {
    @apply grid;
    @apply grid-cols-1;
    @apply gap-4;
    @apply items-stretch;
    @screen lg {
      @apply grid-cols-2;
    }
    @screen 2xl {
      @apply grid-cols-3;
    }
  }

  &__empty {
    @apply flex;
    @apply flex-col;
    @apply items-center;
    @apply text-center;

    i {
      @apply text-4xl;
      @apply text-bluegray-300;
      @apply mb-3;
    }

    h3 {
      @apply font-semibold;
      @apply text-bluegray-600;
    }

    p {
      @apply text-sm;
      @apply text-bluegray-400;
      @apply mt-1;
    }
  }
}
</style>
